import { useEffect, useState, useCallback } from "react";
import { Search, Plus, Trash2, Sparkles, Edit3 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const COLORS = ["#FDE047", "#86EFAC", "#FDBA74", "#D8B4FE", "#7DD3FC", "#F9A8D4"];

function WordDialog({ open, onClose, categories, onSaved, initial }) {
  const [term, setTerm] = useState(initial?.term || "");
  const [definition, setDefinition] = useState(initial?.definition || "");
  const [example, setExample] = useState(initial?.example || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [categoryId, setCategoryId] = useState(initial?.category_id || "");
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  if (!open) return null;

  const aiSuggest = async () => {
    if (!term.trim()) { toast.error("Enter a word first"); return; }
    setAiLoading(true);
    try {
      const r = await api.post("/words/define", { term: term.trim() });
      setDefinition(r.data.definition || "");
      setExample(r.data.example || "");
      toast.success("Definition suggested");
    } catch (e) {
      toast.error("AI suggest failed");
    } finally {
      setAiLoading(false);
    }
  };

  const save = async () => {
    if (!term.trim() || !definition.trim()) { toast.error("Term and definition required"); return; }
    setLoading(true);
    try {
      const body = { term, definition, example, notes, category_id: categoryId || null };
      if (initial?.id) {
        await api.put(`/words/${initial.id}`, body);
      } else {
        await api.post("/words", body);
      }
      onSaved(); onClose();
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-slate-900 rounded-lg w-full max-w-lg p-6" style={{ boxShadow: "8px 8px 0 0 #0F172A" }} onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-2xl font-black">{initial ? "Edit word" : "New word"}</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs uppercase font-bold tracking-widest">Term</label>
            <div className="flex gap-2 mt-1">
              <input
                data-testid="word-term-input"
                value={term}
                onChange={(e)=>setTerm(e.target.value)}
                className="nb-input"
                placeholder="e.g. Photosynthesis"
                spellCheck="true"
                autoCorrect="on"
                autoCapitalize="words"
                lang="en"
              />
              <button onClick={aiSuggest} disabled={aiLoading} data-testid="word-ai-suggest-btn" className="nb-btn-secondary inline-flex items-center gap-2 whitespace-nowrap">
                <Sparkles className="w-4 h-4" strokeWidth={2.5}/> {aiLoading ? "…" : "AI"}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase font-bold tracking-widest">Definition</label>
            <textarea
              data-testid="word-def-input"
              value={definition}
              onChange={(e)=>setDefinition(e.target.value)}
              rows={3}
              className="nb-input mt-1"
              placeholder="Clear, simple definition"
              spellCheck="true"
              autoCorrect="on"
              autoCapitalize="sentences"
              lang="en"
            />
          </div>
          <div>
            <label className="text-xs uppercase font-bold tracking-widest">Example sentence</label>
            <input
              value={example}
              onChange={(e)=>setExample(e.target.value)}
              className="nb-input mt-1"
              spellCheck="true"
              autoCorrect="on"
              autoCapitalize="sentences"
              lang="en"
            />
          </div>
          <div>
            <label className="text-xs uppercase font-bold tracking-widest">Notes</label>
            <input
              value={notes}
              onChange={(e)=>setNotes(e.target.value)}
              className="nb-input mt-1"
              spellCheck="true"
              autoCorrect="on"
              autoCapitalize="sentences"
              lang="en"
            />
          </div>
          <div>
            <label className="text-xs uppercase font-bold tracking-widest">Category</label>
            <select value={categoryId} onChange={(e)=>setCategoryId(e.target.value)} className="nb-input mt-1">
              <option value="">— uncategorised —</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={save} disabled={loading} data-testid="word-save-btn" className="nb-btn-primary flex-1">{loading ? "Saving…" : "Save"}</button>
          <button onClick={onClose} className="nb-btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function Glossary() {
  const [words, setWords] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [dialog, setDialog] = useState({ open: false, edit: null });

  const load = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (filter) params.category_id = filter;
    const [w, c] = await Promise.all([api.get("/words", { params }), api.get("/categories")]);
    setWords(w.data); setCats(c.data);
  }, [q, filter]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    await api.delete(`/words/${id}`);
    toast.success("Word deleted");
    load();
  };

  const catColor = (cid) => cats.find(c => c.id === cid)?.color || "#E2E8F0";
  const catName = (cid) => cats.find(c => c.id === cid)?.name;

  const setCategory = async (wordId, newCategoryId) => {
    const prev = words;
    // optimistic update
    setWords(words.map(w => w.id === wordId ? { ...w, category_id: newCategoryId || null } : w));
    try {
      await api.put(`/words/${wordId}`, { category_id: newCategoryId || null });
      toast.success(newCategoryId ? `Moved to ${catName(newCategoryId)}` : "Removed from category");
    } catch {
      setWords(prev);
      toast.error("Could not update category");
    }
  };

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Glossary</p>
          <h1 className="font-display text-4xl font-black mt-1">Your words</h1>
        </div>
        <button onClick={() => setDialog({ open: true, edit: null })} data-testid="add-word-btn" className="nb-btn-primary inline-flex items-center gap-2">
          <Plus className="w-4 h-4" strokeWidth={2.5}/> Add word
        </button>
      </header>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={2.5}/>
          <input data-testid="word-search" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search words or definitions" className="nb-input pl-9"/>
        </div>
        <select data-testid="word-filter" value={filter} onChange={(e)=>setFilter(e.target.value)} className="nb-input max-w-[220px]">
          <option value="">All categories</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {words.length === 0 ? (
        <div className="nb-card p-12 text-center">
          <p className="font-display text-xl font-bold">No words yet.</p>
          <p className="text-sm text-slate-600 mt-1">Add one above or scan a textbook page.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {words.map((w) => (
            <article key={w.id} data-testid={`word-card-${w.term}`} className="nb-card p-5">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-xl font-bold">{w.term}</h3>
                <label
                  data-testid={`category-picker-${w.term}`}
                  className="relative inline-flex items-center cursor-pointer shrink-0"
                  title="Change category"
                >
                  <span
                    className={`nb-tag whitespace-nowrap pr-6 ${w.category_id ? "" : "bg-slate-100 text-slate-500 border-dashed"}`}
                    style={w.category_id ? { background: catColor(w.category_id) } : undefined}
                  >
                    {w.category_id ? catName(w.category_id) : "+ category"}
                  </span>
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold opacity-70">▼</span>
                  <select
                    aria-label="Category"
                    value={w.category_id || ""}
                    onChange={(e) => setCategory(w.id, e.target.value || null)}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  >
                    <option value="">— uncategorised —</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
              </div>
              <p className="text-sm text-slate-800 mt-2 leading-relaxed">{w.definition}</p>
              {w.example && <p className="text-sm text-slate-500 mt-2 italic">"{w.example}"</p>}
              <div className="mt-4 flex gap-2">
                <button onClick={() => setDialog({ open: true, edit: w })} className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3"><Edit3 className="w-3 h-3" strokeWidth={2.5}/> Edit</button>
                <button onClick={() => del(w.id)} data-testid={`delete-word-${w.term}`} className="nb-btn-danger text-xs inline-flex items-center gap-1 py-2 px-3"><Trash2 className="w-3 h-3" strokeWidth={2.5}/> Delete</button>
              </div>
            </article>
          ))}
        </div>
      )}

      <WordDialog open={dialog.open} onClose={() => setDialog({ open: false, edit: null })} categories={cats} onSaved={load} initial={dialog.edit}/>
    </div>
  );
}
